<template>
  <div class="directory-page">
    <skill-header :title="title" @back="handleBack" />
    <main class="directory-list">
      <directory-node
        v-for="(node, index) in directories"
        :key="node.categoryId || node.id || index"
        :node="node"
        :resource-type="resourceType"
        @leaf="openQuestions"
      />
      <div v-if="!loading && !directories.length" class="empty-state">
        <span>暂无数据</span>
      </div>
    </main>
  </div>
</template>

<script>
import {
  getResourceCategory,
  getWrongAnswerCategory,
  getSkillCenterFullTree
} from '@/api/subject/index.js'
import { closeSkillCenterWeb } from '@/utils/skillCenter'
import { Toast } from 'vant'
import DirectoryNode from './DirectoryNode.vue'
import SkillHeader from './SkillHeader.vue'
import {
  collectLeafIds,
  filterResourceTree,
  findSkillCenterCategory,
  nestedResourceTree,
  nodeLabel as getNodeLabel,
  nodeOrganCategoryId,
  questionStationResources,
  skillCenterFirstLevelCategories,
  skillCenterTreeData,
  treeChildren
} from './shared'

export default {
  name: 'SkillCenterQuestionDirectory',
  components: { DirectoryNode, SkillHeader },
  data() {
    return {
      loading: true,
      directories: [],
      stationList: [],
      currentCategory: null,
      currentStation: null
    }
  },
  computed: {
    mode() {
      const route = this.$route.path
      if (route.includes('wrong')) return 'wrong'
      if (route.includes('note')) return 'note'
      return 'collect'
    },
    title() {
      if (this.mode === 'wrong') return '我的错题'
      if (this.mode === 'note') return '我的笔记'
      return '我的收藏'
    },
    majorType() {
      return this.$route.query.majorType || ''
    },
    resourceType() {
      if (this.mode === 'wrong') return 'wrong'
      if (this.mode === 'note') return 'note'
      return 'favorite'
    }
  },
  created() {
    if (String(this.$route.query.preview) === '1') {
      this.directories = [
        { id: '1', label: '影像诊断', questionCount: 4, children: [{ id: '11', label: '影像诊断基础', questionCount: 4, children: [] }] },
        {
          id: '2', label: '心电图', questionCount: 8, expanded: true, children: [
            { id: '21', label: '第一章 绪论', doneNum: 50, questionCount: 82, children: [] },
            { id: '22', label: '第二章 细胞的基本功能', doneNum: 18, questionCount: 122, children: [] },
            { id: '23', label: '第三章 血液', doneNum: 30, questionCount: 68, children: [] }
          ]
        },
        { id: '3', label: '医德医风', questionCount: 5, children: [{ id: '31', label: '医德医风基础', questionCount: 5, children: [] }] },
        { id: '4', label: '病例分析', questionCount: 8, children: [{ id: '41', label: '病例分析基础', questionCount: 8, children: [] }] }
      ]
      this.loading = false
      return
    }
    this.loadDirectory()
  },
  methods: {
    async loadDirectory() {
      this.loading = true
      const toast = Toast.loading({ message: '加载中', duration: 0, forbidClick: true })
      try {
        const res = await getSkillCenterFullTree()
        const data = skillCenterTreeData(res)
        if (!data.length) throw new Error('技能中心目录为空')
        const categoryList = skillCenterFirstLevelCategories(data)
        this.currentCategory = findSkillCenterCategory(
          data,
          this.$route.query.categoryOrganId,
          this.majorType
        ) || categoryList[0] || null
        this.applyCategory(this.currentCategory)
      } catch (error) {
        // 新技能中心树不可用时保留旧入口兼容。
        try {
          const params = {
            majorType: this.majorType,
            type: this.mode === 'note' ? 1 : 0
          }
          const res = this.mode === 'wrong'
            ? await getWrongAnswerCategory('', params)
            : await getResourceCategory('', params)
          const data = res && Array.isArray(res.data) ? res.data : []
          this.directories = data.length === 1 && data[0].children
            ? data[0].children
            : data
        } catch (fallbackError) {
          Toast(fallbackError && fallbackError.msg ? fallbackError.msg : '目录加载失败')
        }
      } finally {
        toast.clear()
        this.loading = false
      }
    },
    applyCategory(category) {
      this.currentCategory = category
      this.stationList = treeChildren(category).filter((item) => {
        return item && item.virtualNode === false && Number(item.storageLevel) === 2 &&
          (Number(item.type) === 2 || nestedResourceTree(item, 'question').length)
      })
      const stationId = this.$route.query.organCategoryId
      this.currentStation = this.stationList.find((item) => nodeOrganCategoryId(item) === String(stationId || '')) || this.stationList[0] || null
      this.applyStation(this.currentStation)
    },
    applyStation(station) {
      this.currentStation = station
      const resource = questionStationResources(this.currentCategory, nodeOrganCategoryId(station)).find((item) => {
        return nodeOrganCategoryId(item.station) === nodeOrganCategoryId(station)
      })
      this.directories = resource
        ? filterResourceTree(resource.roots, this.resourceType)
        : []
    },
    openQuestions(payload) {
      const node = payload && payload.node ? payload.node : payload
      const chapterName = payload && payload.chapterName ? payload.chapterName : ''
      const categoryIds = collectLeafIds(node)
      if (!categoryIds.length) {
        Toast('当前目录暂无题目')
        return
      }
      this.$router.push({
        path: '/skillCenter/subjectHomeWork',
        query: {
          categoryIds: JSON.stringify(categoryIds),
          categoryId: String(node.categoryId || node.id || ''),
          categoryOrganId: nodeOrganCategoryId(this.currentCategory),
          organCategoryId: nodeOrganCategoryId(this.currentStation),
          source: this.mode,
          majorType: this.currentCategory ? this.currentCategory.categoryType : this.majorType,
          label: getNodeLabel(node),
          chapterName,
          skillCenter: 1,
          from: this.$route.path
        }
      })
    },
    handleBack() {
      closeSkillCenterWeb(this, `skillCenter${this.mode}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.directory-page {
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background-color: #ffffff;
  background-image: url("~@/assets/images/skillCenterQuestion/header-background.png");
  background-position: top center;
  background-repeat: no-repeat;
  background-size: 100% auto;
  color: #111111;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
  box-sizing: border-box;
}

.directory-list {
  padding: 20px 19px 30px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 128px);
  color: #a2a6af;
  font-size: 14px;
}

@media (min-width: 600px) {
  .directory-page {
    width: 375px;
    margin: 0 auto;
  }
}

</style>
